import get from 'lodash/fp/get';
import flow from 'lodash/fp/flow';
import map from 'lodash/fp/map';
import pick from 'lodash/fp/pick';
import values from 'lodash/fp/values';
import getOr from 'lodash/fp/getOr';
import find from 'lodash/fp/find';
import every from 'lodash/fp/every';
import isEmpty from 'lodash/fp/isEmpty';
import { format, parseISO, differenceInDays, startOfDay, isWithinInterval } from 'date-fns/fp';

import { BOOKED, getSourceName, PENDING_MOD_BOOKING } from '../utils';
import { trackInteraction, trackLink, trackPage } from './utils';
import {
	getAnalyticsBookingFor,
	getDefaultBookingInfo,
	getDefaultData,
	getEnhancementInformation,
	getHashedEmailData,
	getPropertyData,
	getCartState,
	getRecoFlags,
} from './utils/index';
import { triggerLinkView } from '@micro-apps/env';
import Cookies from 'js-cookie';
import { getPPAnalyticsValues } from './utils/getPPAnalyticsValues';

function getRecommendationsCookie() {
	try {
		const cookieValue = Cookies.get('reco-clicks');
		if (!cookieValue) {
			return { properties: [], ancillaries: [], roomUpgrades: [], datalakeRecoIds: [] };
		}
		return JSON.parse(atob(cookieValue));
	} catch {
		// Fail silently
		return { properties: [], ancillaries: [], roomUpgrades: [], datalakeRecoIds: [] };
	}
}

const safeBase64Decode = value => {
	// cookies sometimes use base64url (-_). Normalize to base64 (+/)
	const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
	// pad to multiple of 4
	const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);

	try {
		return typeof atob === 'function'
			? atob(padded)
			: Buffer.from(padded, 'base64').toString('utf-8');
	} catch {
		// if it isn't base64, just return original
		return value;
	}
};

const getRecoCookieExpiry = () => {
	const in365Days = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);

	const raw = Cookies.get('UserToken');
	if (!raw) return in365Days;

	try {
		const decoded = safeBase64Decode(raw);

		const parsed = JSON.parse(decoded);

		// Rule: if persistentStateExpiry exists -> keep 365 days
		if (parsed.persistentStateExpiry) return in365Days;

		// Else mirror profileExpiry if valid
		if (parsed.profileExpiry) {
			const d = new Date(parsed.profileExpiry);
			if (!Number.isNaN(d.getTime())) return d;
		}

		return in365Days;
	} catch {
		return in365Days;
	}
};

export const removeUsedRecoFromCookie = ({ booking }) => {
	try {
		const cookieValue = Cookies.get('reco-clicks');

		if (!cookieValue) return;

		const parsed = JSON.parse(atob(cookieValue));

		const bookedPropertyCodes =
			booking?.hotelProducts?.map(p => p?.orsHotelCode?.toLowerCase()) || [];

		const bookedEnhancementCodes = booking?.eventProducts?.map(p => p?.code?.toLowerCase()) || [];

		const updatedRecoIds = (parsed.datalakeRecoIds || []).filter(item => {
			const propertyMatch =
				item?.type === 'property' && bookedPropertyCodes.includes(item?.ors?.toLowerCase());

			const enhancementMatch =
				item?.type !== 'property' && bookedEnhancementCodes.includes(item?.sku?.toLowerCase());

			return !(propertyMatch || enhancementMatch);
		});

		const updatedCookie = {
			...parsed,
			datalakeRecoIds: updatedRecoIds,
		};

		Cookies.set('reco-clicks', btoa(JSON.stringify(updatedCookie)), {
			path: '/',
			expires: getRecoCookieExpiry(),
		});
	} catch (err) {
		console.error('Failed to update reco cookie', err);
	}
};

function removeFromCartIssue({ productId, booking }) {
	const checkInDate = get(['hotelProducts', 0, 'checkInDate'])(booking);
	const checkOutDate = get(['hotelProducts', 0, 'checkOutDate'])(booking);

	const isOnlyStay =
		flow(getOr([], 'hotelProducts'), every(['productType', 'HOTEL']))(booking) &&
		flow(getOr([], 'eventProducts'), isEmpty)(booking);

	if (!checkInDate || !checkOutDate || isOnlyStay) {
		return 'no error';
	}

	const bookingDates = {
		start: parseISO(checkInDate),
		end: parseISO(checkOutDate),
	};

	return flow(
		getOr([], 'eventProducts'),
		find({ productId }),
		getOr('', 'date'),
		parseISO,
		isWithinInterval(bookingDates),
		isWithinStay => {
			if (!isWithinStay) {
				return 'product dates not aligned with stay';
			}
			return 'no error';
		}
	)(booking);
}

const INTERACTION_TYPES = {
	SHOPPING_CART: 'shopping cart',
};

const INTERACTION_NAMES = {
	ENHANCE_YOUR_STAY: 'enhance your stay',
	CART_OVERLAY: 'cart_overlay',
};

const getUpcomingStayValue = flow(
	get(['hotelProducts', 0]),
	pick(['checkInDate', 'checkOutDate']),
	values,
	map(parseISO),
	dates => {
		if (dates.length === 0) {
			return 'no upcoming stay';
		}

		const today = startOfDay(new Date());
		const diff = differenceInDays(today)(dates[0]);

		if (diff > 0) {
			return diff.toString();
		}

		const diff2 = differenceInDays(today)(dates[1]);
		if (diff2 >= 0) {
			return 'in stay';
		}

		return 'no upcoming stay';
	}
);

const CANCELLED_BOOKING_STATUSES = [
	'CANCELLED',
	'PENDING_CANCEL',
	'PENDING_MOD_CANCEL',
	'FAILED_BOOKING',
	'FAILED_CANCEL',
];

const isLiveBooking = booking =>
	!CANCELLED_BOOKING_STATUSES.includes(get(['bookingStatus'])(booking));

const checkInTime = booking => {
	const checkInDate = get(['hotelProducts', 0, 'checkInDate'])(booking);
	return checkInDate ? parseISO(checkInDate).getTime() : Number.POSITIVE_INFINITY;
};

export const getNearestBooking = (bookings = []) =>
	bookings.filter(isLiveBooking).reduce((nearest, booking) => {
		if (!nearest) return booking;
		return checkInTime(booking) < checkInTime(nearest) ? booking : nearest;
	}, null);

/**
 * @param {{ bookings?: unknown[], hotelCode?: string, isAnonymous?: boolean }} options
 */
export const getStayAnalyticsVariables = ({
	bookings = [],
	hotelCode,
	isAnonymous = false,
} = {}) => {
	if (isAnonymous) {
		return { upcoming_stay: 'na', stay_status: 'na' };
	}

	const upcomingStay = getUpcomingStayValue(getNearestBooking(bookings));

	const bookingsForProperty = hotelCode
		? bookings.filter(booking => get(['hotelProducts', 0, 'hotelCode'])(booking) === hotelCode)
		: [];
	const nearestAtProperty = getNearestBooking(bookingsForProperty);
	const stayStatus = nearestAtProperty ? getUpcomingStayValue(nearestAtProperty) : 'na';

	return {
		upcoming_stay: upcomingStay,
		stay_status: stayStatus === 'no upcoming stay' ? 'na' : stayStatus,
	};
};

export const enhanceYourStayScreenViewed = ({
	locale,
	booking,
	profile,
	content,
	ppProfile,
	defaultAaAccount,
	tealiumEnvironment,
	ppMode,
} = {}) => {
	// These variables should be added into the page view call.
	// All existing room, rate, and enhancement data from the itinerary should be populated in the appropriate variables.
	const event_name = 'enhance_stay';
	const recoCookie = getRecommendationsCookie();
	let ancillaryAvailable =
		Array.isArray(booking?.eventProducts) && booking.eventProducts.length > 0;

	trackPage({
		event_name,
		...getDefaultData({ locale, profile }),
		...getDefaultBookingInfo({ booking, recoCookie }),
		...getEnhancementInformation({ booking }),
		...getPropertyData({ content }),
		...getRecoFlags({ booking, recoCookie, isEnhancement: false }),
		...(ancillaryAvailable ? getRecoFlags({ booking, recoCookie, isEnhancement: true }) : {}),
		page_detail: 'enhance-your-stay',
		...getPPAnalyticsValues({
			ppProfile,
			defaultAaAccount,
			tealiumEnvironment,
			ppMode,
		}),
	});
};

export const skipButtonPress = () => {
	const interaction_type = INTERACTION_TYPES.SHOPPING_CART;
	const interaction_name = INTERACTION_NAMES.ENHANCE_YOUR_STAY;
	const interaction_detail = 'skip this step';

	trackInteraction(interaction_type, interaction_name, interaction_detail);
};

export const itemInfoModalViewed = ({ modalName } = {}) => {
	const interaction_type = INTERACTION_TYPES.SHOPPING_CART;
	const interaction_name = INTERACTION_NAMES.ENHANCE_YOUR_STAY;
	const interaction_detail = `${modalName} MODAL`;

	trackInteraction(interaction_type, interaction_name, interaction_detail);
};

export const openAddTransportationForm = ({ section } = {}) => {
	const interaction_type = INTERACTION_TYPES.SHOPPING_CART;
	const interaction_name = INTERACTION_NAMES.ENHANCE_YOUR_STAY;
	const interaction_detail = `transportation: add details for ${
		section === 'dropOff' ? 'departure' : 'arrival'
	}`;

	trackInteraction(interaction_type, interaction_name, interaction_detail);
};

export const enhancementAdded = ({
	locale,
	booking,
	profile,
	productId,
	propertyContent,
	enhancementAssetType,
	analyticsContext,
} = {}) => {
	// These variables should fire in a utag.link() call once the enhancement (e.g. transportation, food & drink, or flowers) addition is validated..
	const isAncillary =
		booking?.bookingStatus === BOOKED || booking?.bookingStatus === PENDING_MOD_BOOKING;
	const event_name = isAncillary ? 'enhancement_order' : 'add_to_cart';
	const recoCookie = getRecommendationsCookie();
	const enhancementInformation = getEnhancementInformation({
		productId,
		booking,
		enhancementAssetType,
	});
	const enhancementMerchA = analyticsContext?.enhancementMerchA;

	trackLink(event_name, {
		...getPropertyData({ content: propertyContent }),
		...getDefaultData({ locale, profile, includeSerialId: false }),
		...analyticsContext?.everyPageVariables,
		...enhancementInformation,
		...(analyticsContext?.pageDetail ? { page_detail: analyticsContext.pageDetail } : {}),
		...(analyticsContext?.startDate
			? { enhancement_start_date: [format('yyyy-MM-dd')(analyticsContext.startDate)] }
			: {}),
		...(analyticsContext?.endDate
			? { enhancement_end_date: [format('yyyy-MM-dd')(analyticsContext.endDate)] }
			: {}),
		...(enhancementMerchA
			? {
					enhancement_merch_a: enhancementInformation.enhancement_name.map(() => enhancementMerchA),
				}
			: {}),
		...getRecoFlags({
			productId,
			booking,
			recoCookie,
			isEnhancement: true,
		}),
		upcoming_stay: getUpcomingStayValue(booking),
		order_method: getSourceName(booking),
		cart_state:
			analyticsContext?.cartState ??
			(booking?.bookingStatus !== BOOKED ? getCartState({ booking, productId }) : undefined),
		booking_currency: booking?.hotelProducts?.[0]?.price?.total?.cash?.currencyCode,
		booking_number_rooms: booking?.hotelProducts?.length || 0,
	});

	// For ancillary orders (post-booking), clear any reco ids that have now been purchased so
	// subsequent enhancement views/orders don't keep attributing to the original recommendation click.
	if (isAncillary) {
		removeUsedRecoFromCookie({ booking });
	}
};

export const enhancementOrProductRemoved = ({ locale, booking, profile, productId } = {}) => {
	// These variables should fire on click of the "Remove" link in a utag.link() call.
	const event_name = 'remove_from_cart';
	const recoCookie = getRecommendationsCookie();

	triggerLinkView(event_name, {
		...getDefaultData({ locale, profile, includeSerialId: false }),
		...getEnhancementInformation({ productId, booking }),
		order_method: getSourceName(booking),
		cart_issue: removeFromCartIssue({ productId, booking }),
		// When removing booking, productId will be null, so include room information
		...(!productId ? getDefaultBookingInfo({ booking, recoCookie }) : {}),
		...getRecoFlags({
			productId,
			booking,
			recoCookie,
			isEnhancement: !!productId,
		}),
		booking_number_rooms: booking?.hotelProducts?.length || 0,
	});
};

export const enhancementEditedButtonClicked = ({ locale, booking, profile, name = '' } = {}) => {
	const interaction_type = INTERACTION_TYPES.SHOPPING_CART;
	const interaction_name = INTERACTION_NAMES.ENHANCE_YOUR_STAY;
	const interaction_detail = `${name}: edit details`;

	trackInteraction(interaction_type, interaction_name, interaction_detail);
};

export const enhancementAvailCheck = ({
	startDate,
	endDate,
	propertyContent,
	enhancementName,
	enhancementType,
	enhancementCategory,
	enhancementSubcategory,
	enhancementAssetType,
	enhancementSku,
}) => {
	//get cookie if available
	const recoCookie = getRecommendationsCookie();
	const recoItem =
		recoCookie?.datalakeRecoIds?.find(
			x => x?.sku?.toLowerCase() === enhancementSku.toLowerCase()
		) ?? null;
	const isReco = !!recoItem;
	const datalakeId = isReco ? (recoItem.datalakeRecoId ?? 'na') : 'na';
	trackLink('enhancement_avail_check', {
		...getPropertyData({ content: propertyContent }),
		enhancement_start_date:
			typeof startDate === 'string' ? [startDate] : [format('yyyy-MM-dd')(startDate)],
		enhancement_end_date: typeof endDate === 'string' ? [endDate] : [format('yyyy-MM-dd')(endDate)],
		enhancement_name: [enhancementName],
		reco_enhancement_name: [isReco ? enhancementName : 'na'],
		enhancement_type: [enhancementType],
		enhancement_category: [enhancementCategory],
		enhancement_subcategory: [enhancementSubcategory],
		enhancement_asset_types: [enhancementAssetType],
		enhancement_reco_flag: [isReco ? 'yes' : 'no'],
		reco_enhancement_property_name: [isReco ? recoItem.ors : 'na'],
		reco_enhancement_sku: [isReco ? enhancementSku : 'na'],
		enhancement_sku: [enhancementSku],
		enhancement_datalake_reco_id: [isReco ? datalakeId : 'na'],
	});
};

export const completeBookingScreenViewed = ({
	locale,
	booking,
	profile,
	content,
	ppProfile,
	defaultAaAccount,
	tealiumEnvironment,
	mpeDetails,
	ppMode,
} = {}) => {
	// These variables should be added into the page view call.
	// All existing room, rate, and enhancement data from the itinerary should be populated in the appropriate variables.
	const event_name = 'confirm_stay';
	const recoCookie = getRecommendationsCookie();
	let ancillaryAvailable =
		Array.isArray(booking?.eventProducts) && booking.eventProducts.length > 0;
	const enhancementRecoFlags = ancillaryAvailable
		? getRecoFlags({ booking, recoCookie, isEnhancement: true })
		: {};
	const propertyRecoFlags = getRecoFlags({
		booking,
		recoCookie,
		isEnhancement: false,
	});
	trackPage({
		event_name,
		...getDefaultData({ locale, profile }),
		...getDefaultBookingInfo({ booking, recoCookie }),
		...getEnhancementInformation({ booking }),
		...getPropertyData({
			content,
		}),
		...(mpeDetails?.analyticsData || {}),
		...enhancementRecoFlags,
		...propertyRecoFlags,
		page_detail: 'complete-booking',
		...getPPAnalyticsValues({
			ppProfile,
			defaultAaAccount,
			tealiumEnvironment,
			ppMode,
		}),
	});
};

export const bookingConfirmedScreenViewed = ({
	locale,
	booking,
	profile,
	content,
	mpeDetails,
	bookingFor,
	booking_payment_method,
	defaultAaAccount,
	tealiumEnvironment,
	ppProfile,
	ppMode,
} = {}) => {
	// These variables should be added into the page view call.
	// All existing room, rate, and enhancement data from the itinerary should be populated in the appropriate variables.
	const event_name = 'purchase';
	const booking_role = getAnalyticsBookingFor(bookingFor);
	const recoCookie = getRecommendationsCookie();
	let ancillaryAvailable =
		Array.isArray(booking?.eventProducts) && booking.eventProducts.length > 0;
	trackPage({
		event_name,
		booking_payment_method,
		booking_role,
		...getHashedEmailData({ booking_role, booking }),
		...getDefaultData({ locale, profile }),
		...getDefaultBookingInfo({ booking, recoCookie }),
		...getEnhancementInformation({ booking }),
		...getPropertyData({ content }),
		...(ancillaryAvailable ? getRecoFlags({ booking, recoCookie, isEnhancement: true }) : {}),
		...getRecoFlags({ booking, recoCookie, isEnhancement: false }),
		...(mpeDetails?.analyticsData || {}),
		order_method: 'Web - Shopping',
		booking_id: booking?.hotelProducts?.map(product => product.reservationId || ''),
		page_detail: 'booking-confirmed',
		...getPPAnalyticsValues({
			ppProfile,
			defaultAaAccount,
			tealiumEnvironment,
			ppMode,
		}),
	});
	removeUsedRecoFromCookie({ booking });
};

export const addPersonalizationOrRequests = data => {
	// The personalize_type variable should pass "arrival time", "reason for stay", and/or "comments"
	// to indicate which section(s) of the form the user filled out.
	// If multiple values are required, they may be delimited by a comma, e.g. "arrival time,comments".
	const event_name = 'personalize_stay';
	const personalize_type = Object.keys(data)
		.filter(key => data[key])
		.join(',');

	trackLink(event_name, {
		personalize_type,
	});
};

export const shoppingCartView = ({ locale, booking, profile } = {}) => {
	const event_name = 'shopping_cart_view';
	const recoCookie = getRecommendationsCookie();
	let ancillaryAvailable =
		Array.isArray(booking?.eventProducts) && booking.eventProducts.length > 0;
	let propertyAvailable = Array.isArray(booking?.hotelProducts) && booking.hotelProducts.length > 0;

	triggerLinkView(event_name, {
		...getDefaultData({ locale, profile }),
		...getDefaultBookingInfo({ booking, recoCookie }),
		...getEnhancementInformation({ booking }),
		...(ancillaryAvailable ? getRecoFlags({ booking, recoCookie, isEnhancement: true }) : {}),
		...(propertyAvailable ? getRecoFlags({ booking, recoCookie, isEnhancement: false }) : {}),
		cart_state: getCartState({ booking, productId: null }),
		order_method: getSourceName(booking),
		booking_currency: booking?.hotelProducts?.[0]?.price?.total?.cash?.currencyCode,
	});
};

export const cartModalShown = interaction_detail => {
	const interaction_type = INTERACTION_TYPES.SHOPPING_CART;
	const interaction_name = INTERACTION_NAMES.CART_OVERLAY;

	trackInteraction(interaction_type, interaction_name, interaction_detail);
};

export const signInFormShown = () => {
	trackLink('form_view', {
		form_name: 'sign-in',
	});
};

export const signInFormSubmitted = ({ loginMethod, loginType, enablePersistentLogin }) => {
	trackLink('form_submit,login', {
		form_name: 'sign-in',
		login_method: loginMethod,
		login_type: loginType,
		enable_persistent_login: enablePersistentLogin,
	});
};

export const findReservationFormShown = () => {
	trackLink('form_view', {
		form_name: 'find a reservation',
	});
};

export const findReservationFormSubmitted = () => {
	trackLink('form_submit', {
		form_name: 'find a reservation',
	});
};

export const preferredPartnersPreviewQuoteClicked = (data = {}) => {
	trackInteraction('preferred partners', 'complete booking', 'preview quote', undefined, data);
};

export const preferredPartnersShareQuoteClicked = (data = {}) => {
	trackInteraction('preferred partners', 'quote', 'share quote', undefined, data);
};

export default {
	enhanceYourStayScreenViewed,
	skipButtonPress,
	itemInfoModalViewed,
	openAddTransportationForm,
	enhancementAdded,
	enhancementOrProductRemoved,
	enhancementEditedButtonClicked,
	enhancementAvailCheck,
	completeBookingScreenViewed,
	bookingConfirmedScreenViewed,
	addPersonalizationOrRequests,
	shoppingCartView,
	cartModalShown,
	signInFormShown,
	signInFormSubmitted,
	findReservationFormShown,
	findReservationFormSubmitted,
	preferredPartnersPreviewQuoteClicked,
	preferredPartnersShareQuoteClicked,
};
